import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  RankedBreakdownItem,
  RankedSortOrder,
  SalesBreakdownView,
} from 'src/app/models/sales-manager/ranked-breakdown.types';
import { GlobalReportApiService } from './global-report-api.service';
import { HeaderKpisService } from './header-kpis.service';
import { ReportScopeService } from '../report-scope.service';
import { LatestRequestTracker, latestRequestTracker } from 'src/app/utils/reports/latest-request';

interface ViewState {
  data: WritableSignal<RankedBreakdownItem[]>;
  loading: WritableSignal<boolean>;
  /** Scope `(rol, evento)` de lo cargado en esta vista; `null` = nada. */
  loadedKey: WritableSignal<string | null>;
  sort: WritableSignal<RankedSortOrder>;
  /** Scope del request en vuelo; solo vale mientras `loading()`. */
  inFlightKey: string | null;
  request: LatestRequestTracker;
}

/**
 * State for the two backend-ranked breakdown sub-views (category /
 * product-type). One singleton holds independent state per view and caches
 * each: toggling the level-1 selector never refetches an already-loaded view.
 * Data is loaded all-at-once (per_page 100) — no pagination.
 */
@Injectable({ providedIn: 'root' })
export class RankedBreakdownService {
  private readonly state: Record<SalesBreakdownView, ViewState> = {
    category: this.emptyViewState(),
    'product-type': this.emptyViewState(),
  };

  constructor(
    private globalReportApi: GlobalReportApiService,
    private headerKpisService: HeaderKpisService,
    private reportScope: ReportScopeService,
  ) {}

  dataFor(view: SalesBreakdownView): Signal<RankedBreakdownItem[]> {
    return this.state[view].data;
  }

  loadingFor(view: SalesBreakdownView): Signal<boolean> {
    return this.state[view].loading;
  }

  sortFor(view: SalesBreakdownView): Signal<RankedSortOrder> {
    return this.state[view].sort;
  }

  /**
   * Loads the view only if what it holds is not the current `(role, event)`
   * scope. Toggling the level-1 selector reuses the cache; coming back with a
   * different role refetches, because the backend narrows by role.
   */
  loadIfNeeded(view: SalesBreakdownView): void {
    const s = this.state[view];
    const key = this.cacheKey();
    const inFlight = s.loading() && s.inFlightKey === key;
    if (!inFlight && s.loadedKey() !== key) {
      this.load(view);
    }
  }

  private cacheKey(): string {
    return this.reportScope.keyFor(this.headerKpisService.eventId);
  }

  changeSort(view: SalesBreakdownView, sort: RankedSortOrder): void {
    if (this.state[view].sort() === sort) return;
    this.state[view].sort.set(sort);
    this.load(view);
  }

  /** Pull-to-refresh: re-fetch every view that had already been loaded. */
  reload(): void {
    (Object.keys(this.state) as SalesBreakdownView[]).forEach((view) => {
      if (this.state[view].loadedKey() !== null) {
        this.load(view);
      }
    });
  }

  resetAllState(): void {
    (Object.keys(this.state) as SalesBreakdownView[]).forEach((view) => {
      const s = this.state[view];
      s.data.set([]);
      s.loading.set(false);
      s.loadedKey.set(null);
      s.sort.set('most_sold');
    });
  }

  private load(view: SalesBreakdownView): void {
    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    const s = this.state[view];
    const key = this.cacheKey();
    // Rol u orden nuevos con un request en vuelo: la respuesta vieja se descarta.
    const isLatest = s.request.start();
    s.inFlightKey = key;
    s.loading.set(true);

    this.requestFor(view, eventId, s.sort()).subscribe({
      next: ({ data = [] }: { data?: RankedBreakdownItem[] }) => {
        if (!isLatest()) return;
        s.data.set(data);
        s.loadedKey.set(key);
      },
      error: () => {
        if (isLatest()) s.loading.set(false);
      },
      complete: () => {
        if (isLatest()) s.loading.set(false);
      },
    });
  }

  private requestFor(
    view: SalesBreakdownView,
    eventId: number | string,
    sort: RankedSortOrder,
  ): Observable<{ data?: RankedBreakdownItem[] }> {
    return view === 'category'
      ? this.globalReportApi.getRankedCategories(eventId, sort)
      : this.globalReportApi.getRankedProductTypes(eventId, sort);
  }

  private emptyViewState(): ViewState {
    return {
      data: signal<RankedBreakdownItem[]>([]),
      loading: signal(false),
      loadedKey: signal<string | null>(null),
      sort: signal<RankedSortOrder>('most_sold'),
      inFlightKey: null,
      request: latestRequestTracker(),
    };
  }
}
